import * as React from 'react';
import { Dropdown as BootstrapDropdown } from 'react-bootstrap';

import { isNullOrUndefined } from '@acceligentllc/shared/utils/extensions';

import { bemBlock } from 'af-utils/bem.util';

import type { OwnProps as PlainDropdownProps, DropdownOptionType } from 'af-components/Controls/Dropdown';

import styles from './styles.module.scss';
import type { CellError } from '../../error.types';

type Props<T extends DropdownOptionType> = PlainDropdownProps<T> & {
	elementKey: string;
	placeholder?: string;
	className?: string;
	error?: CellError;
	readonly?: boolean;
	bold?: boolean;
};

function DisplayCell<T extends DropdownOptionType>(props): React.ReactElement<Props<T>> {

	const {
		bold,
		inputClassName,
		elementKey,
		value,
		className,
		disabled,
		isWhite,
		withCaret,
		labelKey,
		options,
		isActionDropdown,
		withBorder = false,
		defaultValue,
		placeholder,
		error,
		valueKey,
	} = props;

	// QA id is automatically generated to help with automated tests
	// We want to avoid accidentally forgetting to place an id on an element, making it harder for QA to write automated tests, so we keep it generated
	// Styling should never come from id, anyway
	const id = React.useMemo(() => (
		`accqa__dropdown__display__${elementKey}`
	), [elementKey]);

	const getContainerClassName = React.useMemo(() => {
		let cn = className ?? `${styles['dropdown-cell']}`;
		if (error) {
			cn = `${cn} ${styles['error-wrapper']}`;
			if (error.type === 'required') {
				cn = `${cn} ${styles['error-placeholder']}`;
			}

		}
		if (bold) {
			cn = `${cn} ${styles.bold}`;
		}
		return cn;
	}, [bold, className, error]);

	const selectedOption = React.useMemo(() => {
		if (!value || !options) {
			return null;
		}
		const _selectedOption = options.find((option) => option[valueKey] === value);
		return _selectedOption;
	}, [options, value, valueKey]);

	const renderToggle = React.useCallback(() => {
		if (isActionDropdown) {
			return <span className="icon-actions" />;
		}

		if ('renderMenuItem' in props && selectedOption) {
			return (
				<div className="dropdown-menu__selected">
					{props.renderMenuItem(selectedOption)}
				</div>
			);
		}

		if (isNullOrUndefined(selectedOption)) {
			return (
				<div className="dropdown-menu__selected">
					<span className="dropdown-menu__selected-text">
						{placeholder ?? 'Select'}
					</span>
					{withCaret && <span className="icon-down dropdown-menu__with-caret"></span>}
				</div>
			);
		}

		return (
			<div className="dropdown-menu__selected">
				<span className="dropdown-menu__selected-text">
					{selectedOption[labelKey] ?? defaultValue}
				</span>
				{withCaret && <span className="icon-down dropdown-menu__with-caret"></span>}
			</div>
		);
		// it wants to put props in the dependency array - no need for that with props.renderMenuItem
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isActionDropdown, selectedOption, labelKey, defaultValue, withCaret, placeholder, props.renderMenuItem]);

	const dropdownToggleContainerClassName = React.useMemo(() => {
		const bemResult = bemBlock('dropdown-toggle', {
			bordered: !!withBorder && !isActionDropdown,
			white: !!isWhite && !error,
		});

		return `${inputClassName ?? ''} ${bemResult} ${isActionDropdown ? styles['actions-3dot'] : ''}`;
	}, [withBorder, isActionDropdown, isWhite, error, inputClassName]);

	const renderCell = React.useMemo(() => (
		<div className={getContainerClassName} key={elementKey}>
			<BootstrapDropdown>
				<BootstrapDropdown.Toggle
					className={dropdownToggleContainerClassName}
					disabled={disabled}
					id={id}
				>
					{renderToggle()}
				</BootstrapDropdown.Toggle>
			</BootstrapDropdown>
		</div>
	), [getContainerClassName, elementKey, dropdownToggleContainerClassName, disabled, id, renderToggle]);

	return renderCell;
}

export default React.memo(DisplayCell) as typeof DisplayCell;
